import { supabase } from '../lib/supabase';

/**
 * Chart of Accounts Service
 * Provides CRUD operations for IFRS-compliant chart of accounts
 */

// Get all accounts for an organization with hierarchy
export const getChartOfAccounts = async (organizationId = null) => {
  try {
    let query = supabase?.from('chart_of_accounts')?.select(`
        id,
        account_code,
        account_name,
        account_description,
        account_type,
        account_subtype,
        normal_balance,
        parent_account_id,
        account_level,
        full_path,
        ifrs_section,
        ifrs_subsection,
        is_control_account,
        is_bank_account,
        is_cash_account,
        allows_posting,
        opening_balance,
        current_balance,
        status,
        is_system_account,
        created_at,
        updated_at,
        organization:organizations(id, name)
      `)?.eq('status', 'active')?.order('full_path');

    if (organizationId) {
      query = query?.eq('organization_id', organizationId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching chart of accounts:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Service error in getChartOfAccounts:', error);
    return { data: [], error: { message: 'Failed to fetch chart of accounts' } };
  }
};

// Get account hierarchy for display
export const getAccountHierarchy = async (organizationId) => {
  try {
    const { data, error } = await supabase?.rpc('get_account_hierarchy', { org_uuid: organizationId });

    if (error) {
      console.error('Error fetching account hierarchy:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Service error in getAccountHierarchy:', error);
    return { data: [], error: { message: 'Failed to fetch account hierarchy' } };
  }
};

// Create new account
export const createAccount = async (accountData) => {
  try {
    // Validate required fields
    if (!accountData?.organization_id || !accountData?.account_code || 
        !accountData?.account_name || !accountData?.account_type || 
        !accountData?.account_subtype) {
      return { 
        data: null, 
        error: { message: 'Missing required fields: organization_id, account_code, account_name, account_type, account_subtype' } 
      };
    }

    // Set normal balance based on account type if not provided
    if (!accountData?.normal_balance) {
      const normalBalances = {
        'asset': 'debit',
        'expense': 'debit', 
        'liability': 'credit',
        'equity': 'credit',
        'revenue': 'credit'
      };
      accountData.normal_balance = normalBalances?.[accountData?.account_type] || 'debit';
    }

    // Set IFRS section if not provided
    if (!accountData?.ifrs_section) {
      const ifrsSections = {
        'asset': accountData?.account_subtype?.includes('current') ? 'Current Assets' : 'Non-current Assets',
        'liability': accountData?.account_subtype?.includes('current') ? 'Current Liabilities' : 'Non-current Liabilities',
        'equity': 'Equity',
        'revenue': 'Revenue',
        'expense': 'Expenses'
      };
      accountData.ifrs_section = ifrsSections?.[accountData?.account_type];
    }

    const { data, error } = await supabase?.from('chart_of_accounts')?.insert([{
        ...accountData,
        created_by: (await supabase?.auth?.getSession())?.data?.session?.user?.id
      }])?.select()?.single();

    if (error) {
      console.error('Error creating account:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Service error in createAccount:', error);
    return { data: null, error: { message: 'Failed to create account' } };
  }
};

// Update account
export const updateAccount = async (accountId, updates) => {
  try {
    if (!accountId) {
      return { data: null, error: { message: 'Account ID is required' } };
    }

    const { data, error } = await supabase?.from('chart_of_accounts')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', accountId)?.select()?.single();

    if (error) {
      console.error('Error updating account:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Service error in updateAccount:', error);
    return { data: null, error: { message: 'Failed to update account' } };
  }
};

// Deactivate account (soft delete)
export const deactivateAccount = async (accountId) => {
  try {
    const { data, error } = await supabase?.from('chart_of_accounts')?.update({ 
        status: 'inactive',
        updated_at: new Date()?.toISOString()
      })?.eq('id', accountId)?.select()?.single();

    if (error) {
      console.error('Error deactivating account:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Service error in deactivateAccount:', error);
    return { data: null, error: { message: 'Failed to deactivate account' } };
  }
};

// Get account balances for a period
export const getAccountBalances = async (organizationId, year = null, month = null) => {
  try {
    let query = supabase?.from('account_balances')?.select(`
        id,
        account_id,
        period_year,
        period_month,
        period_end_date,
        opening_balance,
        closing_balance,
        period_debit_total,
        period_credit_total,
        is_period_closed,
        account:chart_of_accounts(
          account_code,
          account_name,
          account_type,
          account_subtype
        )
      `)?.eq('organization_id', organizationId)?.order('period_year', { ascending: false })?.order('period_month', { ascending: false });

    if (year !== null) {
      query = query?.eq('period_year', year);
    }

    if (month !== null) {
      query = query?.eq('period_month', month);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching account balances:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Service error in getAccountBalances:', error);
    return { data: [], error: { message: 'Failed to fetch account balances' } };
  }
};

// Generate trial balance
export const generateTrialBalance = async (organizationId, asOfDate = null) => {
  try {
    const { data, error } = await supabase?.rpc('generate_trial_balance', { 
        org_uuid: organizationId,
        as_of_date: asOfDate || new Date()?.toISOString()?.split('T')?.[0]
      });

    if (error) {
      console.error('Error generating trial balance:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Service error in generateTrialBalance:', error);
    return { data: [], error: { message: 'Failed to generate trial balance' } };
  }
};

// Get account templates
export const getAccountTemplates = async (countryCode = 'INT') => {
  try {
    let query = supabase?.from('account_templates')?.select(`
        id,
        template_name,
        template_description,
        country_code,
        template_data,
        is_ifrs_compliant,
        created_at
      `)?.eq('is_ifrs_compliant', true)?.order('template_name');

    if (countryCode !== 'ALL') {
      query = query?.eq('country_code', countryCode);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching account templates:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Service error in getAccountTemplates:', error);
    return { data: [], error: { message: 'Failed to fetch account templates' } };
  }
};

// Apply account template to organization
export const applyAccountTemplate = async (organizationId, templateId) => {
  try {
    // Get template data
    const { data: template, error: templateError } = await supabase?.from('account_templates')?.select('template_data')?.eq('id', templateId)?.single();

    if (templateError || !template?.template_data) {
      return { data: null, error: templateError || { message: 'Template not found' } };
    }

    // Create accounts from template
    const accounts = [];
    const categories = template?.template_data?.categories || [];

    for (const category of categories) {
      const categoryAccounts = category?.accounts || [];
      for (const account of categoryAccounts) {
        accounts?.push({
          organization_id: organizationId,
          account_code: account?.code,
          account_name: account?.name,
          account_type: category?.type,
          account_subtype: account?.subtype,
          normal_balance: ['asset', 'expense']?.includes(category?.type) ? 'debit' : 'credit',
          is_control_account: account?.control || false,
          is_bank_account: account?.is_bank || false,
          is_cash_account: account?.subtype === 'cash_and_equivalents',
          allows_posting: !account?.control,
          ifrs_section: category?.type === 'asset' ? (account?.subtype?.includes('current') ? 'Current Assets' : 'Non-current Assets') :
            category?.type === 'liability'? (account?.subtype?.includes('current') ? 'Current Liabilities' : 'Non-current Liabilities') :
            category?.type?.charAt(0)?.toUpperCase() + category?.type?.slice(1),
          created_by: (await supabase?.auth?.getSession())?.data?.session?.user?.id
        });
      }
    }

    if (accounts?.length === 0) {
      return { data: null, error: { message: 'No accounts found in template' } };
    }

    const { data, error } = await supabase?.from('chart_of_accounts')?.insert(accounts)?.select();

    if (error) {
      console.error('Error applying account template:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Service error in applyAccountTemplate:', error);
    return { data: null, error: { message: 'Failed to apply account template' } };
  }
};

// Get accounts by type for dropdowns
export const getAccountsByType = async (organizationId, accountType = null, allowsPosting = true) => {
  try {
    let query = supabase?.from('chart_of_accounts')?.select('id, account_code, account_name, account_type, account_subtype, current_balance')?.eq('organization_id', organizationId)?.eq('status', 'active');

    if (accountType) {
      query = query?.eq('account_type', accountType);
    }

    if (allowsPosting) {
      query = query?.eq('allows_posting', true);
    }

    query = query?.order('account_code');

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching accounts by type:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Service error in getAccountsByType:', error);
    return { data: [], error: { message: 'Failed to fetch accounts by type' } };
  }
};

// Validate IFRS compliance
export const validateIFRSCompliance = async (organizationId) => {
  try {
    const { data, error } = await supabase?.rpc('validate_ifrs_sections')?.eq('organization_id', organizationId)?.single();

    if (error) {
      console.error('Error validating IFRS compliance:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Service error in validateIFRSCompliance:', error);
    return { data: null, error: { message: 'Failed to validate IFRS compliance' } };
  }
};

// Real-time subscription for chart of accounts changes
export const subscribeToChartChanges = (organizationId, callback) => {
  const channel = supabase?.channel('chart-of-accounts-changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'chart_of_accounts',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        console.debug('Chart of accounts change detected:', payload);
        callback?.(payload);
      }
    )?.subscribe();

  return () => supabase?.removeChannel(channel);
};

// Account types and subtypes for form options
export const ACCOUNT_TYPE_OPTIONS = [
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
  { value: 'equity', label: 'Equity' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'expense', label: 'Expense' }
];

export const ACCOUNT_SUBTYPE_OPTIONS = {
  asset: [
    { value: 'current_asset', label: 'Current Asset' },
    { value: 'non_current_asset', label: 'Non-current Asset' },
    { value: 'fixed_asset', label: 'Property, Plant & Equipment' },
    { value: 'intangible_asset', label: 'Intangible Asset' },
    { value: 'investment', label: 'Investment' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'receivables', label: 'Trade and Other Receivables' },
    { value: 'cash_and_equivalents', label: 'Cash and Cash Equivalents' }
  ],
  liability: [
    { value: 'current_liability', label: 'Current Liability' },
    { value: 'non_current_liability', label: 'Non-current Liability' },
    { value: 'long_term_debt', label: 'Long-term Debt' },
    { value: 'accrued_liability', label: 'Accrued Liability' },
    { value: 'payables', label: 'Trade and Other Payables' }
  ],
  equity: [
    { value: 'share_capital', label: 'Share Capital' },
    { value: 'retained_earnings', label: 'Retained Earnings' },
    { value: 'other_equity', label: 'Other Equity' },
    { value: 'reserves', label: 'Reserves' }
  ],
  revenue: [
    { value: 'operating_revenue', label: 'Operating Revenue' },
    { value: 'other_revenue', label: 'Other Revenue' },
    { value: 'financial_income', label: 'Financial Income' }
  ],
  expense: [
    { value: 'cost_of_sales', label: 'Cost of Sales' },
    { value: 'operating_expense', label: 'Operating Expense' },
    { value: 'administrative_expense', label: 'Administrative Expense' },
    { value: 'selling_expense', label: 'Selling Expense' },
    { value: 'financial_expense', label: 'Financial Expense' },
    { value: 'other_expense', label: 'Other Expense' }
  ]
};

export const IFRS_SECTIONS = [
  'Current Assets',
  'Non-current Assets', 
  'Current Liabilities',
  'Non-current Liabilities',
  'Equity',
  'Revenue',
  'Expenses'
];

// Create composite service object for easier importing
export const chartOfAccountsService = {
  getChartOfAccounts,
  getAccountHierarchy,
  createAccount,
  updateAccount,
  deactivateAccount,
  getAccountBalances,
  generateTrialBalance,
  getAccountTemplates,
  applyAccountTemplate,
  getAccountsByType,
  validateIFRSCompliance,
  subscribeToChartChanges
};